import React from 'react'
import {
  Button,
  type ButtonProps,
  IconButton,
  InputAdornment,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { CheckoutTextField } from './fields'
import { checkoutSchema, type CheckoutFields } from './schema'
import type { CheckoutForm } from './useCheckoutForm'
import { monoButtonSx } from './styles'

type TextFieldName = Extract<
  keyof CheckoutFields,
  'firstName' | 'lastName' | 'email' | 'phone' | 'company'
>

type TextFormFieldProps = {
  form: CheckoutForm
  name: TextFieldName
  label: string
  placeholder?: string
  autoComplete?: string
  id?: string
  type?: string
}

export const TextFormField: React.FC<TextFormFieldProps> = ({
  form,
  name,
  label,
  placeholder,
  autoComplete,
  id,
  type,
}) => {
  // union `name` makes shape[name] a zod union TanStack's validator type rejects.
  const onChange = checkoutSchema.shape[name] as never
  return (
    <form.Field name={name} validators={{ onChange }}>
      {(field) => (
        <CheckoutTextField
          id={id}
          type={type}
          label={label}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          error={!field.state.meta.isValid}
          helperText={field.state.meta.errors[0]?.message}
        />
      )}
    </form.Field>
  )
}

export const MonoButton: React.FC<ButtonProps> = ({ sx, ...rest }) => (
  <Button {...rest} sx={[monoButtonSx, ...(Array.isArray(sx) ? sx : [sx])]} />
)

type NumberStepperProps = {
  id?: string
  label: string
  value: number
  min: number
  max: number
  disabled?: boolean
  onChange: (v: number) => void
  suffix?: string
}

export const NumberStepper: React.FC<NumberStepperProps> = ({
  id,
  label,
  value,
  min,
  max,
  disabled,
  onChange,
  suffix = '',
}) => {
  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (disabled) return
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      e.preventDefault()
      onChange(Math.min(max, value + 1))
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      e.preventDefault()
      onChange(Math.max(min, value - 1))
    }
  }
  return (
    <CheckoutTextField
      id={id}
      label={label}
      value={`${value}${suffix}`}
      slotProps={{
        input: {
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                type="button"
                size="small"
                color="primary"
                aria-label={`Decrease ${label}`}
                disabled={disabled || value <= min}
                onClick={() => onChange(Math.max(min, value - 1))}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                color="primary"
                aria-label={`Increase ${label}`}
                disabled={disabled || value >= max}
                onClick={() => onChange(Math.min(max, value + 1))}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
        htmlInput: {
          style: { textAlign: 'center' },
          onKeyDown,
          role: 'spinbutton',
          'aria-valuenow': value,
          'aria-valuemin': min,
          'aria-valuemax': max,
        },
      }}
    />
  )
}
